import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSwipe } from '../context/SwipeContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Send, X, MapPin, MoreHorizontal } from 'lucide-react';
import { getUserPhoto } from '../utils/photoUtils';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MessagesPage = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { matches, messages, typingStatus, sendMessage: sendMessageHTTP, loadMessages, loadMatches, setTyping, handleUnmatchNotification } = useSwipe();
  const { user } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showUnmatchDialog, setShowUnmatchDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const menuRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isInitialLoad = useRef(true);
  const prevConversationLength = useRef(0);
  
  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isPulling = useRef(false);

  const match = matches.find(m => m.id === matchId);
  const conversation = useMemo(() => messages[matchId] || [], [messages, matchId]);
  const isMatchTyping = typingStatus[matchId] || false;

  // Format timestamp for messages
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffMs = now - messageTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    // Just now (< 1 min)
    if (diffMins < 1) return 'Just now';
    
    // Minutes ago (< 60 mins)
    if (diffMins < 60) return `${diffMins}m ago`;
    
    // Hours ago (< 24 hours)
    if (diffHours < 24) return `${diffHours}h ago`;
    
    // Yesterday
    if (diffDays === 1) {
      return `Yesterday ${messageTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    }
    
    // Older messages - show date and time
    if (diffDays < 7) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return `${days[messageTime.getDay()]} ${messageTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    }
    
    // Very old messages
    return messageTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  useEffect(() => {
    // Load initial messages for this match
    if (matchId) {
      loadMessages(matchId);
    }

    // Set up polling for new messages (every 2.5 seconds)
    const pollingInterval = setInterval(() => {
      if (matchId) {
        loadMessages(matchId);
      }
    }, 2500);

    // Clean up interval on unmount or when matchId changes
    return () => {
      clearInterval(pollingInterval);
    };
  }, [matchId, loadMessages]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Smart auto-scroll: only scroll in specific scenarios
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Check if user is already near the bottom (within 100px)
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    // Scenario 1: Initial load - always scroll to bottom to show latest messages
    if (isInitialLoad.current && conversation.length > 0) {
      scrollToBottom();
      isInitialLoad.current = false;
      prevConversationLength.current = conversation.length;
      return;
    }

    // Scenario 2: User sent a new message (conversation grew and they were near bottom)
    const conversationGrew = conversation.length > prevConversationLength.current;
    if (conversationGrew && isNearBottom) {
      // Only auto-scroll if user is already at/near the bottom
      // This allows scrolling when user sends message or new message arrives while at bottom
      scrollToBottom();
    }

    // Update previous length for next comparison
    prevConversationLength.current = conversation.length;
  }, [conversation, scrollToBottom]);

  // Pull-to-refresh handlers (upswipe from bottom for messages)
  const handleTouchStart = (e) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    // Only enable pull-to-refresh when scrolled to bottom
    const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 10;
    if (isAtBottom) {
      touchStartY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (!isPulling.current) return;
    
    const touchY = e.touches[0].clientY;
    const distance = touchStartY.current - touchY; // Reversed for upswipe
    
    // Only allow upward pull
    if (distance > 0) {
      // Apply resistance
      const maxPull = 100;
      const resistance = Math.min(distance * 0.5, maxPull);
      setPullDistance(resistance);
      
      // Prevent default scrolling when pulling
      if (distance > 10) {
        e.preventDefault();
      }
    }
  };

  const handleTouchEnd = async () => {
    if (!isPulling.current) return;
    
    isPulling.current = false;
    
    // Trigger refresh if pulled up enough (50px threshold)
    if (pullDistance > 50 && !isRefreshing) {
      setIsRefreshing(true);
      
      try {
        await loadMessages(matchId);
        
        // Show success state briefly
        setTimeout(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        }, 500);
      } catch (error) {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      // Snap back if not pulled enough
      setPullDistance(0);
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator (debounced)
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Send typing status
    setTyping(matchId);
    
    // Set timeout to stop sending typing after 4 seconds of no typing
    typingTimeoutRef.current = setTimeout(() => {
      typingTimeoutRef.current = null;
    }, 4000);  // 4 seconds to match backend TYPING_TIMEOUT (5s with 1s buffer)
  };

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Close photo lightbox on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showPhotoLightbox) {
        setShowPhotoLightbox(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showPhotoLightbox]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (showPhotoLightbox) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showPhotoLightbox]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (newMessage.trim() && !isSending) {
      setIsSending(true);
      try {
        // Send via HTTP (polling will fetch it back shortly)
        await sendMessageHTTP(matchId, newMessage);
        setNewMessage('');
        
        // Immediately refresh messages to show sent message
        await loadMessages(matchId);
      } catch (error) {
        // Message send failed - error already handled by toast
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleUnmatch = async () => {
    setIsProcessing(true);
    try {
      await axios.post(
        `${API_URL}/api/matches/unmatch/${matchId}`,
        {},
        { withCredentials: true } // Use cookies instead of token
      );

      toast({
        title: "Unmatched",
        description: `You and ${match.name} are no longer connected`
      });

      // Refresh matches list to remove the unmatched user
      await loadMatches();

      // Navigate back to matches
      navigate('/matches');
    } catch (error) {
      toast({
        title: "Unmatch failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
      setShowUnmatchDialog(false);
    }
  };

  const handleBlock = async () => {
    setIsProcessing(true);
    try {
      await axios.post(
        `${API_URL}/api/matches/block/${match.id}`,
        { reason: blockReason || undefined },
        { withCredentials: true } // Use cookies instead of token
      );

      toast({
        title: "User blocked",
        description: `${match.name} has been blocked and unmatched`
      });

      // Refresh matches list to remove the blocked user
      await loadMatches();

      // Navigate back to matches
      navigate('/matches');
    } catch (error) {
      toast({
        title: "Block failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
      setShowBlockDialog(false);
      setBlockReason('');
    }
  };

  if (!match) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">Match not found</p>
          <Button onClick={() => navigate('/matches')} className="mt-4">
            Back to Matches
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50 pt-24">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/matches')}
            className="flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <img
              src={getUserPhoto(match)}
              alt={match.name}
              className="w-12 h-12 rounded-full object-cover cursor-pointer hover:opacity-90 transition-opacity flex-shrink-0"
              onClick={() => setShowPhotoLightbox(true)}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-gray-900 truncate">
                  {match.name}, {match.age}
                </h2>
                {match.is_online && (
                  <div className="flex items-center gap-1 bg-green-500/20 px-1.5 py-0.5 rounded-full flex-shrink-0">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span className="text-xs text-green-600 font-medium">Online</span>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500 truncate">
                {match.distance_km !== null && match.distance_km !== undefined
                  ? `${Math.round(match.distance_km)} km away`
                  : 'Distance unavailable'}
              </p>
            </div>
          </div>
          
          {/* Three-dot menu */}
          <div className="relative flex-shrink-0" ref={menuRef}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMenu(!showMenu)}
              className="flex-shrink-0"
            >
              <MoreHorizontal className="w-5 h-5" />
            </Button>

            {/* Dropdown Menu */}
            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-30">
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowUnmatchDialog(true);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Unmatch
                </button>
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowBlockDialog(true);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  Block & Report
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 pb-24"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateY(-${pullDistance}px)`, // Negative for upward movement
          transition: isPulling.current ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {/* Pull-to-refresh indicator at bottom */}
        {pullDistance > 0 && (
          <div 
            className="fixed bottom-24 left-0 right-0 flex justify-center pointer-events-none z-50"
            style={{
              transform: `translateY(-${Math.min(pullDistance - 40, 20)}px)`,
              opacity: Math.min(pullDistance / 50, 1)
            }}
          >
            <div className="bg-white rounded-full shadow-xl border border-gray-200 px-5 py-3 flex items-center gap-2">
              {isRefreshing ? (
                <>
                  <div className="w-5 h-5 border-3 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-purple-600 font-semibold">Refreshing...</span>
                </>
              ) : (
                <>
                  <span className="text-xl">↑</span>
                  <span className="text-sm text-gray-700 font-medium">
                    {pullDistance > 50 ? 'Release to refresh' : 'Pull up to refresh'}
                  </span>
                </>
              )}
            </div>
          </div>
        )}
        
        {conversation.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 mb-4">Start the conversation!</p>
            <p className="text-sm text-gray-400">Say hi to {match.name}</p>
          </div>
        ) : (
          conversation.map((message, index) => {
            const isOwn = message.sender_id === user.id;
            const isRead = message.read;
            
            // Find the last read message from current user
            const lastReadMessageIndex = [...conversation]
              .reverse()
              .findIndex((msg) => msg.sender_id === user.id && msg.read);
            const lastReadIndex = lastReadMessageIndex !== -1 
              ? conversation.length - 1 - lastReadMessageIndex 
              : -1;
            
            // Only show "Seen" on the last read message from current user
            const shouldShowSeen = isOwn && isRead && index === lastReadIndex;
            
            return (
              <div
                key={message.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
                  <div
                    className={`max-w-xs md:max-w-md px-4 py-2 rounded-2xl ${
                      isOwn
                        ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white'
                        : 'bg-white text-gray-900 shadow-sm'
                    }`}
                  >
                    <p className="text-sm">{message.text}</p>
                  </div>
                  {/* Timestamp and Seen indicator */}
                  <div className="flex items-center gap-2 mt-1 px-1">
                    <span className="text-xs text-gray-500">
                      {formatTimestamp(message.timestamp)}
                    </span>
                    {shouldShowSeen && (
                      <span className="text-xs text-gray-500">· Seen</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        
        {/* Typing Indicator */}
        {isMatchTyping && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-4 py-2 rounded-2xl">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{match.name} is typing</span>
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input - Fixed to bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-30">
        <form onSubmit={handleSend} className="flex gap-2 max-w-4xl mx-auto">
          <Input
            value={newMessage}
            onChange={handleInputChange}
            placeholder={`Message ${match.name}...`}
            className="flex-1"
            disabled={isSending}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="sentences"
          />
          <Button
            type="submit"
            size="icon"
            className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white flex-shrink-0"
            disabled={!newMessage.trim() || isSending}
          >
            <Send className="w-5 h-5" />
          </Button>
        </form>
      </div>

      {/* Photo Lightbox Modal */}
      {showPhotoLightbox && match && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setShowPhotoLightbox(false)}
        >
          {/* Modal Content */}
          <div 
            className="relative max-w-4xl w-full max-h-[90vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowPhotoLightbox(false)}
              className="absolute -top-12 right-0 p-2 text-white hover:bg-white/10 rounded-full transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Photo */}
            <div className="relative bg-gray-900 rounded-lg overflow-hidden">
              <img
                src={getUserPhoto(match)}
                alt={match.name}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
              
              {/* Photo Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6">
                <div className="text-white">
                  {/* Name, Age, and Online Status */}
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-2xl font-bold">
                      {match.name}, {match.age}
                    </h2>
                    {match.is_online && (
                      <div className="flex items-center gap-1 bg-green-500/20 px-2 py-1 rounded-full">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-green-400 font-medium">Online</span>
                      </div>
                    )}
                  </div>

                  {/* Location/Distance */}
                  {match.distance_km !== null && match.distance_km !== undefined && (
                    <div className="flex items-center gap-2 text-white/90 mb-2">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">
                        {Math.round(match.distance_km)} km away
                      </span>
                    </div>
                  )}

                  {/* Bio - Full version */}
                  {match.bio && (
                    <p className="text-sm text-white/90 italic">
                      "{match.bio}"
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Helper Text */}
            <p className="text-center text-white/60 text-sm mt-4">
              Click outside or press ESC to close
            </p>
          </div>
        </div>
      )}

      {/* Unmatch Confirmation Dialog */}
      {showUnmatchDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Unmatch {match.name}?</h3>
            <p className="text-gray-600 mb-6">
              You won't be able to message each other anymore. You may see each other again in the future.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowUnmatchDialog(false)}
                disabled={isProcessing}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUnmatch}
                disabled={isProcessing}
                className="flex-1 bg-gray-900 hover:bg-gray-800 text-white"
              >
                {isProcessing ? 'Unmatching...' : 'Unmatch'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Block & Report Dialog */}
      {showBlockDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Block {match.name}?</h3>
            <p className="text-gray-600 mb-4">
              They won't be able to see you or match with you again. This action cannot be undone.
            </p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (optional)
              </label>
              <select
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select a reason...</option>
                <option value="inappropriate">Inappropriate behavior</option>
                <option value="spam">Spam or scam</option>
                <option value="harassment">Harassment</option>
                <option value="fake">Fake profile</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBlockDialog(false);
                  setBlockReason('');
                }}
                disabled={isProcessing}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBlock}
                disabled={isProcessing}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {isProcessing ? 'Blocking...' : 'Block & Report'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
